// APIConfigPanel — runtime tunables for /api/external/v1/* and a viewer for
// the most recent access-log rows. All fields persist into app_config and
// take effect on the next request (no service restart needed).
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  InputNumber,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ClearOutlined, ControlOutlined, HistoryOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { api, type APIAccessLogRow, type APISettings } from "../api";

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Rate-limit form  (External API Configuration card)
// ---------------------------------------------------------------------------

interface RateLimitFormProps {
  settings: APISettings;
  loading: boolean;
  onSaved: (updated: APISettings) => void;
}

function RateLimitForm({ settings, loading, onSaved }: RateLimitFormProps) {
  const [form] = Form.useForm<Pick<APISettings, "rate_limit_rps" | "rate_limit_burst">>();
  const [saving, setSaving] = useState(false);

  // Sync form when settings load/change.
  useEffect(() => {
    form.setFieldsValue({
      rate_limit_rps:   settings.rate_limit_rps,
      rate_limit_burst: settings.rate_limit_burst,
    });
  }, [form, settings]);

  const submit = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      const updated = await api.updateAPISettings({
        ...settings,
        rate_limit_rps:   vals.rate_limit_rps,
        rate_limit_burst: vals.rate_limit_burst,
      });
      onSaved(updated);
      message.success("Settings saved");
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" disabled={loading} style={{ maxWidth: 480 }}>
      <Form.Item
        name="rate_limit_rps"
        label="Rate limit — requests per second (per key)"
        rules={[{ required: true, type: "number", min: 0, max: 100000 }]}
        extra="0 disables rate limiting entirely. Default: 60."
      >
        <InputNumber min={0} max={100000} step={10} style={{ width: 180 }} />
      </Form.Item>

      <Form.Item
        name="rate_limit_burst"
        label="Burst size (tokens)"
        rules={[{ required: true, type: "number", min: 1, max: 1000000 }]}
        extra="Allows brief spikes above the steady-state rate. Default: 120."
      >
        <InputNumber min={1} max={1000000} step={10} style={{ width: 180 }} />
      </Form.Item>

      <Button type="primary" icon={<SaveOutlined />} onClick={submit} loading={saving}>
        Save
      </Button>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Audit log table + retention controls  (Recent API Access card)
// ---------------------------------------------------------------------------

function statusTag(code: number) {
  if (code >= 500) return <Tag color="red">{code}</Tag>;
  if (code === 429) return <Tag color="orange">{code}</Tag>;
  if (code === 401 || code === 403) return <Tag color="volcano">{code}</Tag>;
  if (code >= 400) return <Tag color="gold">{code}</Tag>;
  return <Tag color="green">{code}</Tag>;
}

interface UsageLogProps {
  settings: APISettings;
  settingsLoading: boolean;
  onSaved: (updated: APISettings) => void;
}

function UsageLog({ settings, settingsLoading, onSaved }: UsageLogProps) {
  const [rows, setRows]       = useState<APIAccessLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [retDays, setRetDays] = useState(settings.audit_retention_days);
  const [retSaving, setRetSaving] = useState(false);
  const [flushing, setFlushing]   = useState(false);

  // Keep local retention field in sync with loaded settings.
  useEffect(() => {
    setRetDays(settings.audit_retention_days);
  }, [settings.audit_retention_days]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.apiKeyUsage({ limit: 200 }));
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRetention = async () => {
    setRetSaving(true);
    try {
      const updated = await api.updateAPISettings({
        ...settings,
        audit_retention_days: retDays,
      });
      onSaved(updated);
      message.success("Retention saved");
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setRetSaving(false);
    }
  };

  const handleFlush = async () => {
    setFlushing(true);
    try {
      const res = await api.flushAPIAuditLog();
      message.success(`Flushed ${res.deleted} audit log row${res.deleted !== 1 ? "s" : ""}`);
      load();
    } catch (e) {
      if (e instanceof Error) message.error("Flush failed: " + e.message);
    } finally {
      setFlushing(false);
    }
  };

  const columns: ColumnsType<APIAccessLogRow> = [
    {
      title: "Time",
      dataIndex: "ts",
      width: 160,
      render: (v: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {new Date(v * 1000).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Key",
      dataIndex: "key_id",
      width: 60,
      render: (v: number) => <Text code style={{ fontSize: 11 }}>#{v}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      render: (v: number) => statusTag(v),
    },
    {
      title: "Method",
      dataIndex: "method",
      width: 80,
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: "Path",
      dataIndex: "path",
      render: (v: string) => (
        <Text style={{ fontSize: 12, fontFamily: "monospace" }}>{v}</Text>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration_ms",
      width: 100,
      align: "right",
      render: (v: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{v} ms</Text>
      ),
    },
    {
      title: "IP",
      dataIndex: "ip",
      width: 140,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 12, fontFamily: "monospace" }}>{v}</Text>
      ),
    },
  ];

  return (
    <div>
      {/* Retention + flush toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 12, flexWrap: "wrap",
      }}>
        <Text style={{ fontSize: 13, whiteSpace: "nowrap" }}>Audit log retention:</Text>
        <Space.Compact size="small">
          <InputNumber
            min={0}
            max={3650}
            value={retDays}
            onChange={(v) => setRetDays(v ?? 0)}
            disabled={settingsLoading}
            style={{ width: 70 }}
          />
          <Button disabled style={{ cursor: "default", color: "var(--muted)", background: "var(--bg-1)" }}>
            days
          </Button>
        </Space.Compact>
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>(0 = keep forever)</Text>
        <Button
          size="small"
          icon={<SaveOutlined />}
          loading={retSaving}
          disabled={settingsLoading}
          onClick={saveRetention}
        >
          Save
        </Button>
        <Popconfirm
          title="Flush audit log?"
          description="Delete ALL API access log rows. Cannot be undone."
          onConfirm={handleFlush}
          okText="Flush"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" icon={<ClearOutlined />} loading={flushing} danger>
            Flush Now
          </Button>
        </Popconfirm>
        <div style={{ flex: 1 }} />
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20, size: "small" }}
        locale={{ emptyText: "No external API requests yet." }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Combined panel
// ---------------------------------------------------------------------------

const DEFAULT_SETTINGS: APISettings = {
  rate_limit_rps:       60,
  rate_limit_burst:     120,
  audit_retention_days: 30,
};

export default function APIConfigPanel() {
  const [settings, setSettings]           = useState<APISettings>(DEFAULT_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      setSettings(await api.apiSettings());
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  return (
    <>
      <Card
        size="small"
        title={
          <Space>
            <ControlOutlined />
            <span style={{ fontWeight: 700 }}>External API Configuration</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <RateLimitForm
          settings={settings}
          loading={settingsLoading}
          onSaved={setSettings}
        />
      </Card>

      <Card
        size="small"
        title={
          <Space>
            <HistoryOutlined />
            <span style={{ fontWeight: 700 }}>Recent API Access</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <UsageLog
          settings={settings}
          settingsLoading={settingsLoading}
          onSaved={setSettings}
        />
      </Card>
    </>
  );
}
